/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { Layer47SaveService } from './layer4-7.wizard.save.service';
import { Layer47WizardService } from './layer4-7.wizard.service';
import { LogService, UtilsService } from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { TemplateModel } from '../../../models';
import { WizardService } from './wizard.service';
import { WizardTitleEnum } from '../../../enums';

@Injectable()
export class WizardSaveService {
    /**
     * Available service ids and names.
     */
    private existingServices: any[] = [];

    constructor(
        private layer47SaveService: Layer47SaveService,
        private layer47WizardService: Layer47WizardService,
        private logService: LogService,
        private utilsService: UtilsService,
        private wizardService: WizardService,
    ) {
    }

    /**
     * Get services from controller state and create a template object.
     */
    public convertControllerStateObjectForExport(): Observable<any> {
        return new Observable(observer => {
            // Store for services objects
            const services: any = this.wizardService.controllerState;
            const selectedTemplate: TemplateModel = this.utilsService.duplicateObject(this.wizardService.selectedTemplate);
            const { existingServices, propertiesToExclude, helperService } = this.getSetUpValues(selectedTemplate.type);
            this.existingServices = existingServices;
            const saveTemplateObject = this.createSaveTemplateObject(services, selectedTemplate, propertiesToExclude);
            const template: TemplateModel = this.createTemplateModel(selectedTemplate, saveTemplateObject);

            if (!helperService) {
                this.logService.error(
                    'WizardSaveService:convertControllerStateObjectForExport',
                    'Unsupported template type',
                    selectedTemplate.type
                );
                observer.next(template);
                observer.complete();
            } else {
                helperService.validateSaveTemplate(saveTemplateObject, template)
                    .subscribe({
                        next: (template: TemplateModel): void => {
                            observer.next(template);
                            observer.complete();
                        }
                    });
            }
        });
    }

    /**
     * Get set up values.
     * @param type
     * @private
     */
    private getSetUpValues(type: string): any {
        switch (type) {
            case 'layer4':
            case 'layer7':
            case 'monitor':
                return {
                    existingServices: this.layer47WizardService.getListOfServiceIdsAndNames(),
                    propertiesToExclude: this.layer47SaveService.propertiesToExclude(),
                    helperService: this.layer47SaveService
                };
            case 'gslb':
                return [];
            default:
                return [];
        }
    }

    /**
     * Create save template object from services
     * @param services
     * @param selectedTemplate
     * @param propertiesToExclude
     * @private
     */
    private createSaveTemplateObject(services: any, selectedTemplate: TemplateModel, propertiesToExclude: string[]): any {
        const saveTemplateObject = {};

        // Loop through service types in the controller state.
        for (const serviceType in services) {
            if (services.hasOwnProperty(serviceType) && serviceType !== 'status') {
                this.utilsService.createArrayIfNotExist(saveTemplateObject, serviceType);
                if (selectedTemplate.builds && serviceType !== WizardTitleEnum.HEADERS && serviceType !== 'undefined') {
                    saveTemplateObject[serviceType].push(
                        this.convertServiceToBuildsTemplate(selectedTemplate.builds[serviceType][0], services[serviceType][0])
                    );
                } else {
                    // Loop through the array of each service type e.g. frontend 1, frontend 2 etc.
                    for (const serviceObject of services[serviceType]) {
                        for (const property in serviceObject) {
                            if (serviceObject.hasOwnProperty(property)) {
                                if (property === 'id' &&
                                    this.utilsService.isServiceId(serviceObject[property]) &&
                                    !serviceObject[property].startsWith('temp-')
                                ) {
                                    serviceObject[property] = '';
                                }
                                if (!propertiesToExclude.includes(property)) {
                                    this.replaceServiceIdWithName(serviceObject[property]);
                                }
                            }
                        }

                        // TODO do we want to check for placeholders and preserver them?
                        saveTemplateObject[serviceType].push(serviceObject);
                    }
                }
            }
        }

        return saveTemplateObject;
    }

    /**
     * Replace service IDs with names
     * @param serviceField
     * @private
     */
    private replaceServiceIdWithName(serviceField: any): void {
        if (serviceField?.value === undefined || serviceField?.value === null) {
            this.logService.debug('WizardSaveService:replaceServiceIdWithName', 'ServiceField has no value');
        } else {
            const regexPattern = /[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}-[a-zA-Z0-9]{3}/;

            if (typeof serviceField.value === 'string') {
                const id: string = this.checkFieldForIdMatch(serviceField.value, regexPattern);
                serviceField.value = id ? this.getValueNameById(id) || serviceField.value : serviceField.value;
            }

            if (Array.isArray(serviceField.value)) {
                let counter = 1;
                const newValue = [];
                for (const arrayItem of serviceField.value) {
                    if (typeof arrayItem === 'string') {
                        const id = this.checkFieldForIdMatch(arrayItem, regexPattern);
                        const name = this.getValueNameById(id);
                        if (name) {
                            newValue.push(name);
                        } else if (id) {
                            newValue.push('$' + counter);
                            counter++;
                        } else {
                            newValue.push(arrayItem);
                        }
                    } else {
                        newValue.push(arrayItem);
                    }
                }

                serviceField.value = newValue;
            }
        }
    }

    /**
     * Check for ID
     * @param serviceFieldValue
     * @param regexPattern
     * @private
     */
    private checkFieldForIdMatch(serviceFieldValue: string, regexPattern: RegExp): string {
        const match: any = serviceFieldValue.match(regexPattern);
        if (match && match.length) {
            return match[0];
        }
    }

    // TODO investigate if this can be removed... 04/06/23 not sure it will ever get hit....
    /**
     * Look up ID in services
     * @param id
     * @private
     */
    private getValueNameById(id: string): string {
        const service = this.existingServices.find(item => item.id === id);
        if (service?.name) {
            return service.name;
        }
    }

    /**
     * Set the form values to the builds part of the template.
     * @param serviceBuilds
     * @param serviceObject
     * @private
     */
    private convertServiceToBuildsTemplate(serviceBuilds: any, serviceObject: any): any {
        for (const property in serviceBuilds) {
            if (this.isPropertyValid(serviceBuilds, serviceObject, property)) {
                const input: any = serviceBuilds[property];
                // We only want to set values for items that don't have template values
                if (input.value === ''
                    || input.value === undefined
                    || input.value === null
                    || (input.value && !input.value.length)) {
                    input.value = serviceObject[property].value ? serviceObject[property].value : serviceObject[property];
                }
            }
        }

        return serviceBuilds;
    }

    /**
     * Check if the property is valid
     * @param serviceBuilds
     * @param serviceObject
     * @param property
     * @private
     */
    private isPropertyValid(serviceBuilds: any, serviceObject: any, property: any): any {
        const serviceProperty: any = serviceObject[property];

        return serviceBuilds.hasOwnProperty(property) && property !== 'title' && serviceProperty || typeof serviceProperty === 'boolean';
    }

    /**
     * Update template model from save template object.
     * @param selectedTemplate
     * @param saveTemplateObject
     * @private
     */
    private createTemplateModel(selectedTemplate: TemplateModel, saveTemplateObject: any): TemplateModel {
        const template: TemplateModel = this.utilsService.duplicateObject(selectedTemplate);

        // If there is a builds part of the template update if with the saveTemplateObject.
        if (template.builds) {
            template.builds = saveTemplateObject;
        } else {
            template.sections = saveTemplateObject;
        }

        return template;
    }
}
